import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  Link,
  useRouteLoaderData,
  useLocation,
  useNavigate,
  useMatches,
} from "react-router";
import { useEffect, useMemo, useState } from "react";
import { COOKIE_CONSENT_OPEN_EVENT, CookieConsentBanner } from "@mealmanager/cookie-consent";

import type { Route } from "./+types/root";
import "antd/dist/antd.css";
import "./globals.css";
import Apollo from "./components/Apollo";
import AppShell, { type NavKey } from "./components/AppShell";
import { freshGreenTheme } from "./components/AppShell/Theme";
import Config, { type ConfigValues } from "./components/Config";
import NotFound from "./components/NotFound";
import HeapAnalytics from "./components/HeapAnalytics";
import type { NormalizedCacheObject } from "@apollo/client";
import { fetchConfigForRequest } from "./utils/configLoader";
import { getBlogFlags } from "./utils/blogFlags";

export const links: Route.LinksFunction = () => [
  { rel: "icon", href: "/favicon.ico", sizes: "any" },
  { rel: "shortcut icon", href: "/favicon.ico" },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Nunito:ital,wght@0,400..800;1,400..800&display=swap",
  },
];

const COOKIE_BANNER_HIDDEN_PATHS = new Set(["/privacy", "/cookies"]);

type LoaderData = {
  config: ConfigValues | null;
  blogEnabled: boolean;
};

export async function loader({ request, context }: Route.LoaderArgs) {
  const config = await fetchConfigForRequest(request, context);
  const { blogEnabled } = getBlogFlags(config);
  const payload: LoaderData = { config, blogEnabled };
  return Response.json(payload);
}

export function Layout({ children }: { children: React.ReactNode }) {
  // Middleware errors (e.g. the blog layout's 404) are thrown before this route's loader runs,
  // so loader data is absent when the error boundary renders inside Layout.
  const loaderData = useRouteLoaderData<typeof loader>("root") as LoaderData | undefined;
  const { config = null, blogEnabled = false }: Partial<LoaderData> = loaderData ?? {};
  const matches = useMatches();
  const apolloState = useMemo<NormalizedCacheObject | undefined>(() => {
    return matches.reduce<NormalizedCacheObject | undefined>((acc, match) => {
      const data = match.loaderData as { apolloState?: NormalizedCacheObject } | undefined;
      if (data?.apolloState) {
        return { ...(acc ?? {}), ...data.apolloState };
      }
      return acc;
    }, undefined);
  }, [matches]);
  // Theme is now configured inside AppShell
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname || "/";
  const [showCookieConsentOnLegalPage, setShowCookieConsentOnLegalPage] = useState(false);
  const getNavKey = (pathname: string): NavKey => {
    switch (true) {
      case pathname === "/":
        return "home";
      case pathname.startsWith("/recipes"):
        return "recipes";
      case pathname === "/blog" || pathname.startsWith("/blog/"):
        return "blog";
      default:
        return "home";
    }
  };

  const getNavPath = (key: NavKey): string => {
    switch (key) {
      case "home":
        return "/";
      case "recipes":
        return "/recipes";
      case "blog":
        return "/blog";
      default:
        return "/";
    }
  };

  const active: NavKey = getNavKey(pathname);
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleOpen = () => {
      setShowCookieConsentOnLegalPage(true);
    };

    window.addEventListener(COOKIE_CONSENT_OPEN_EVENT, handleOpen);
    return () => window.removeEventListener(COOKIE_CONSENT_OPEN_EVENT, handleOpen);
  }, []);

  useEffect(() => {
    if (!COOKIE_BANNER_HIDDEN_PATHS.has(pathname)) {
      setShowCookieConsentOnLegalPage(false);
    }
  }, [pathname]);

  const onNavigate = (key: NavKey) => {
    navigate(getNavPath(key));
  };
  const showCookieConsentBanner = !COOKIE_BANNER_HIDDEN_PATHS.has(pathname) || showCookieConsentOnLegalPage;

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        {/* Prevent iOS double-tap / focus zoom (combined with 16px base font in globals.css) */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover, maximum-scale=1, user-scalable=no"
        />
        <Meta />
        <Links />
      </head>
      <body style={{ ["--app-page-bg" as string]: freshGreenTheme.components.Layout.footerBg }}>
        <Config initialConfig={config ?? undefined} renderBeforeLoad={!loaderData}>
          <HeapAnalytics />
          <Apollo initialState={apolloState} renderBeforeReady={!loaderData}>
            <AppShell active={active} onNavigate={onNavigate} blogEnabled={blogEnabled}>
              {children}
            </AppShell>
          </Apollo>
        </Config>
        {showCookieConsentBanner ? (
          <CookieConsentBanner
            cookieDomain={config?.cookieDomain}
            renderLink={({ href, children, onClick }) => (
              <Link
                to={href}
                onClick={(event) => {
                  event.preventDefault();
                  setShowCookieConsentOnLegalPage(false);
                  onClick?.();
                  navigate(href);
                }}
                style={{ color: "#86efac" }}
              >
                {children}
              </Link>
            )}
          />
        ) : null}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function HydrateFallback() {
  return null;
}

export default function App() {
  return <Outlet />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  // Thrown 404s (disabled blog, bad preview link) get the same page as unmatched URLs.
  if (isRouteErrorResponse(error) && error.status === 404) return <NotFound />;

  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (process.env.NODE_ENV === "development" && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <div style={{ maxWidth: 700, margin: '48px auto 0', padding: '24px', background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <h1 style={{ marginBottom: 16 }}>{message}</h1>
      <p style={{ marginBottom: 16 }}>{details}</p>
      {stack && (
        <pre style={{ width: '100%', padding: 16, overflowX: 'auto', background: '#f9faf9', borderRadius: 8 }}>
          <code>{stack}</code>
        </pre>
      )}
    </div>
  );
}
